import { Grid } from '@mui/material';
import { useSelector } from '@services/hooks/redux-hooks';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';

import EsfirusSelect, { EsfirusSelectPropsOption } from '@ui/Select/EsfirusSelect';
import DatePickerUI from '../../../../components/ui/DatePickerUI/DatePickerUI';

import { BORRADORES_AVISOS_STATUS_TYPES, BORRADORES_AVISOS_STATUS_TYPES_LABELS, OPCIONES_USER_FOR_ME_FILTER, OPCIONES_USER_FOR_ME_LABELS } from '@screens/Avisos/constants';
import { getUserId, isAdmin, isJefeObra, isSupervisor } from '@services/hooks/UserHook';

import { sortEmpleados } from '@helpers/utils';
import { Obra } from '@models/obra';
import "./ListadoAvisosFilters.scss";

const ListadoAvisosFilters = ({ filters, setFilters, items, isForBorrador = false, availableFilters }: any) => {
  const empresas = useSelector(state => state.empresas.list)
  const obras = useSelector(state => state.obras.list)
  const noticeStatuses = useSelector(state => state.notices.listStatuses)
  const noticeTypes = useSelector(state => state.notices.listTypes)
  const users = useSelector(state => state.users.list)

  // Posibilidad de deshabilitar los filtros de empleados y gestionar el filtro "para mí".
  const [forMeValue, setForMeValue] = useState<string>(OPCIONES_USER_FOR_ME_FILTER.EMPTY)
  const [disableEmployeeFilters, setDisableEmployeeFilters] = useState<{ origin: boolean, target: boolean, responsible: boolean }>({ origin: false, target: false, responsible: false })

  const userForMeOptions = Object.entries(OPCIONES_USER_FOR_ME_LABELS).map(([value, label]) => ({ label, value }))
  const draftStatusOptions = Object.entries(BORRADORES_AVISOS_STATUS_TYPES_LABELS).map(([value, label]) => ({ label, value }))

  const usersOptions = useCallback(
    (type: string) => {
      const { empresa } = filters;

      const usersByFilter = users.filter(user => {
        const isInternalMatch = user.data.internal?.some(({ empleado, empresa }) =>
          availableFilters?.[type]?.includes(empleado) &&
          availableFilters?.companies?.includes(empresa)
        );

        const isExternalMatch = user.data.external?.some(({ empleado, empresa }) =>
          availableFilters?.[type]?.includes(empleado) &&
          availableFilters?.companies?.includes(empresa)
        );

        return isInternalMatch || isExternalMatch;
      });

      return sortEmpleados(usersByFilter)
        .map((user) => {
          const { internal = [], external = [] } = user.data || {};
          const idsEmpresas = [...internal, ...external].map((e) => +e.empresa);

          if (empresa && !idsEmpresas.includes(+empresa)) return null;

          const idEmpresaInterna = internal[0]?.empresa;
          const empresaObject = empresas.find((e) => e.Empresa === Number(idEmpresaInterna));

          return {
            label: `${user.nombre} ${user.apellidos} - ${empresaObject?.Nombre ?? '-'}`,
            value: user.id,
          };
        })
        .filter(Boolean);
    },
    [users, filters, empresas, availableFilters]
  );

  const obrasOptions = useMemo(() => {
    if (!availableFilters) return [];

    const { works = [], companies = [], branches = [] } = availableFilters;
    const workSet = new Set(works);
    const companiesSet = new Set(companies);
    const branchSet = new Set(branches);

    return obras
      .filter(obra => workSet.has(obra.Obra) && companiesSet.has(obra.Empresa) && branchSet.has(obra.Delegacion))
      .map(obra => ({
        label: obra.Descripcion,
        value: `${obra.Obra}|${obra.Empresa}|${obra.Delegacion}`,
      }));
  }, [obras, availableFilters]);


  const noticesTypes = useMemo(() => {
    if (!availableFilters?.types?.length) return [];

    const typeSet = new Set(availableFilters.types);

    return noticeTypes
      .filter((type) => typeSet.has(type.notification_type))
      .map((type) => ({
        label: type.description,
        value: type.notification_type,
      }));
  }, [noticeTypes, availableFilters?.types]);


  const statuses = useMemo(() => {
    if (!availableFilters?.statuses?.length) return [];

    const uniqueStatus = new Set(availableFilters?.statuses);

    return isForBorrador
      ? draftStatusOptions.filter((e) => uniqueStatus.has(e.value))
      : noticeStatuses
        .filter((e) => uniqueStatus.has(e.status))
        .map((e: any) => ({ label: e.description, value: e.status }))
  }, [isForBorrador, draftStatusOptions, noticeStatuses, availableFilters?.statuses]);

  const companiesOptions = useMemo(() => {
    if (!availableFilters?.companies) return [];

    const uniqueStatus = new Set(availableFilters?.companies);

    return empresas
      .filter((e) => uniqueStatus.has(e.Empresa))
      .map((e: any) => ({ label: e.Nombre, value: e.Empresa }))
  }, [availableFilters?.companies, empresas]);

  const customersOptions = useMemo(() => {
    if (!availableFilters?.clients) return [];

    return availableFilters?.clients.map((id: number) => ({ label: id, value: id }));
  }, [availableFilters?.clients]);

  const userCanSupervise = isAdmin() || isSupervisor()
  const userIsJefeObra = isJefeObra()

  // Opciones del filtro de "para mí" y estados de un borrador.

  useEffect(() => {
    if (!userCanSupervise) {
      handleChangeForMe(userIsJefeObra ? OPCIONES_USER_FOR_ME_FILTER.RESPONSABLE : OPCIONES_USER_FOR_ME_FILTER.DESTINADOS)
    }
  }, [])

  const handleChangeForMe = (e: any) => {
    const userId = String(getUserId())
    setForMeValue(e)

    if (e === OPCIONES_USER_FOR_ME_FILTER.EMPTY) {
      setDisableEmployeeFilters({ origin: false, target: false, responsible: false })
      setFilters((prevFilters: any) => ({ ...prevFilters, empleadoDestino: "", empleadoOrigen: "", empleadoResponsable: "" }))
    } else if (e === OPCIONES_USER_FOR_ME_FILTER.DESTINADOS) {
      setDisableEmployeeFilters({ origin: false, target: true, responsible: false })
      setFilters((prevFilters: any) => ({ ...prevFilters, empleadoDestino: userId, empleadoOrigen: "", empleadoResponsable: "" }))
    } else if (e === OPCIONES_USER_FOR_ME_FILTER.ORIGEN) {
      setDisableEmployeeFilters({ origin: true, target: false, responsible: false })
      setFilters((prevFilters: any) => ({ ...prevFilters, empleadoDestino: "", empleadoOrigen: userId, empleadoResponsable: "" }))
    } else if (e === OPCIONES_USER_FOR_ME_FILTER.RESPONSABLE) {
      setDisableEmployeeFilters({ origin: false, target: false, responsible: true })
      setFilters((prevFilters: any) => ({ ...prevFilters, empleadoDestino: "", empleadoOrigen: "", empleadoResponsable: userId }))
    }
  }

  return (
    <div className='ef-avisos-filtros'>
      <Grid container spacing={1}>
        {/* Filtro de fecha inicio */}
        <Grid item xs={12} sm={6} md={1.5}>
          <DatePickerUI
            value={filters.dateFrom}
            fill={!!filters.dateFrom}
            handleChange={newDate => {
              const dateSelected = moment({ day: newDate.$D, month: newDate.$M, year: newDate.$y })
              const isCorrect = !filters.dateTo || dateSelected.isBefore(filters.dateTo)
              if (isCorrect) setFilters((prevFilters: any) => ({ ...prevFilters, dateFrom: dateSelected }))
            }}
          />
        </Grid>

        {/* Filtro de fecha fin */}
        <Grid item xs={12} sm={6} md={1.5}>
          <DatePickerUI
            value={filters.dateTo}
            fill={!!filters.dateFrom}
            handleChange={newDate => {
              const dateSelected = moment({ day: newDate.$D, month: newDate.$M, year: newDate.$y })
              const isCorrect = !filters.dateFrom || dateSelected.isAfter(filters.dateFrom)
              if (isCorrect) setFilters((prevFilters: any) => ({ ...prevFilters, dateTo: dateSelected }))
            }}
          />
        </Grid>

        {/* Filtro de cliente */}
        <Grid item xs={12} sm={6} md={3}>
          <EsfirusSelect
            options={customersOptions}
            value={filters.customer}
            fill={!!filters.customer}
            change={(e: any) => setFilters((prevFilters: any) => ({ ...prevFilters, customer: e }))}
            placeholder="Todos los clientes"
          />
        </Grid>

        {/* Filtro de empresa */}
        {userCanSupervise && (
          <Grid item xs={12} sm={6} md={3}>
            <EsfirusSelect
              placeholder='Todas las empresas'
              options={companiesOptions}
              value={filters.companyObra || filters.empresa}
              disabled={filters.companyObra}
              fill={!!filters.empresa}
              change={(e: any) => setFilters((prevFilters: any) => ({ ...prevFilters, empresa: e }))}
            />
          </Grid>
        )}

        {/* Filtro de obra */}
        <Grid item xs={12} sm={6} md={3}>
          <EsfirusSelect
            options={obrasOptions as EsfirusSelectPropsOption[]}
            value={filters.obraIdentifier}
            fill={!!filters.obraIdentifier}
            change={(e: string) => {
              const [obra = "", companyObra = "", branch = ""] = e.split('|');

              setFilters((prevFilters: any) => ({
                ...prevFilters,
                obraIdentifier: e,
                obra,
                branch,
                companyObra,
              }));
            }}
            placeholder="Todas las obras"
          />
        </Grid>

        {/* Filtro de combo para mí */}
        <Grid item xs={12} sm={6} md={3}>
          <EsfirusSelect
            placeholder='Seleccionar para mí'
            options={userCanSupervise ? userForMeOptions : userForMeOptions.filter(opt => !!opt.value)}
            disableBlank
            value={forMeValue}
            fill={!!forMeValue}
            change={handleChangeForMe}
          />
        </Grid>

        {/* Filtro de empleado origen */}
        {(userCanSupervise || userIsJefeObra) && (
          <Grid item xs={12} sm={6} md={3}>
            <EsfirusSelect
              options={usersOptions('origin_employees') as EsfirusSelectPropsOption[]}
              value={filters.empleadoOrigen}
              fill={!!filters.empleadoOrigen}
              change={(e: any) => setFilters((prevFilters: any) => ({ ...prevFilters, empleadoOrigen: e }))}
              disabled={disableEmployeeFilters.origin}
              placeholder="Todos los empleados origen"
            />
          </Grid>
        )}

        {/* Filtro de empleado destino */}
        {(userCanSupervise || userIsJefeObra) && (
          <Grid item xs={12} sm={6} md={3}>
            <EsfirusSelect
              options={usersOptions('target_employees') as EsfirusSelectPropsOption[]}
              value={filters.empleadoDestino}
              fill={!!filters.empleadoDestino}
              change={(e: any) => setFilters((prevFilters: any) => ({ ...prevFilters, empleadoDestino: e }))}
              disabled={disableEmployeeFilters.target}
              placeholder="Todos los empleados destino"
            />
          </Grid>
        )}

        {/* Filtro de empleado responsable */}
        {userCanSupervise && (
          <Grid item xs={12} sm={6} md={3}>
            <EsfirusSelect
              options={usersOptions('responsible_employees') as EsfirusSelectPropsOption[]}
              value={filters.empleadoResponsable}
              fill={!!filters.empleadoResponsable}
              change={(e: any) => setFilters((prevFilters: any) => ({ ...prevFilters, empleadoResponsable: e }))}
              disabled={disableEmployeeFilters.responsible}
              placeholder="Todos los empleados responsable"
            />
          </Grid>
        )}

        {/* Filtro de estado */}
        <Grid item xs={12} sm={6} md={3}>
          <EsfirusSelect
            options={statuses}
            value={filters.status}
            fill={!!filters.status}
            change={(e: any) => setFilters((prevFilters: any) => ({ ...prevFilters, status: e }))}
            placeholder="Todos los estados"
          />
        </Grid>

        {/* Filtro de tipo */}
        <Grid item xs={12} sm={6} md={3}>
          <EsfirusSelect
            options={noticesTypes}
            value={filters.type}
            fill={!!filters.type}
            change={(e: any) => setFilters((prevFilters: any) => ({ ...prevFilters, type: e }))}
            placeholder="Todos los tipos"
          />
        </Grid>
      </Grid>
    </div>
  );
}

export default ListadoAvisosFilters;